import { useMemo, useRef, useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Site color palette
const CHART_COLORS = {
    primary: '#e91e63',
    secondary: '#7b809a',
    info: '#1A73E8',
    success: '#4CAF50',
    dark: '#344767',
    light: '#f0f2f5',
    grid: 'rgba(123, 128, 154, 0.2)', // Secondary color with opacity for grid
    text: '#344767', // Dark color for text
    points: '#1A73E8', // Info color for points
};

function PassiveProjects({ projects = [] }) {
    // Proje durumu verilerini hesapla
    const labels = [];
    const values = [];
    let counterPassiveProject = {};

    projects.forEach((project) => {
        const status = project?.status?.tag?.name;
        if (status && status.trim() !== '') {
            if (isNaN(counterPassiveProject[status])) {
                counterPassiveProject[status] = 0;
            }
            counterPassiveProject[status] += 1;
        }
    });

    Object.keys(counterPassiveProject).forEach((key) => {
        labels.push(key);
        values.push(counterPassiveProject[key]);
    });

    const hasData = projects.length > 0 && values.some((value) => value > 0);

    // Chart serisi
    const series = [
        {
            data: values,
            label: 'Pasif Projelerin Durumu',
            curve: 'monotoneX', // Daha yumuşak eğri
            color: CHART_COLORS.info,
            valueFormatter: (value) => `Proje Sayısı: ${value}`,
        },
    ];

    // Responsive chart width için
    const chartContainerRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(0);

    useEffect(() => {
        const updateChartWidth = () => {
            if (chartContainerRef.current) {
                setChartWidth(chartContainerRef.current.offsetWidth);
            }
        };

        // İlk render'da genişliği ayarla
        updateChartWidth();

        // ResizeObserver ile container boyutu değiştiğinde genişliği güncelle
        const resizeObserver = new ResizeObserver(updateChartWidth);
        if (chartContainerRef.current) {
            resizeObserver.observe(chartContainerRef.current);
        }

        // Cleanup
        return () => {
            if (chartContainerRef.current) {
                resizeObserver.unobserve(chartContainerRef.current);
            }
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Card sx={{ height: '100%' }}>
            {useMemo(
                () => (
                    <MDBox
                        ref={chartContainerRef}
                        variant="gradient"
                        bgColor="white"
                        borderRadius="xl"
                        coloredShadow="white"
                        height="12.5rem"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {chartWidth > 0 && hasData ? (
                            <LineChart
                                series={series}
                                width={chartWidth}
                                xAxis={[
                                    {
                                        data: labels,
                                        scaleType: 'point',
                                        tickLabelStyle: {
                                            color: CHART_COLORS.text,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            display: false,
                                        },
                                    },
                                ]}
                                yAxis={[
                                    {
                                        tickLabelStyle: {
                                            color: CHART_COLORS.text,
                                            fontSize: 12,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            strokeDasharray: '5 5',
                                            stroke: CHART_COLORS.grid,
                                        },
                                    },
                                ]}
                                height={220}
                                margin={{ left: 5, right: 10, top: 10, bottom: 30 }}
                                tooltip={{
                                    trigger: 'item', // Her nokta için tooltip göster
                                    formatter: (params) => `${params.label}: ${params.value} proje`,
                                }}
                                slotProps={{
                                    legend: { hidden: true },
                                }}
                                axisHighlight={{
                                    x: 'line',
                                    y: 'line',
                                }}
                                sx={{
                                    '.MuiLineElement-root': {
                                        strokeWidth: 3,
                                        strokeLinecap: 'round', // Yuvarlatılmış çizgi uçları
                                        filter: 'drop-shadow(0px 2px 4px rgba(25, 0, 255, 0.3))', // Primary color shadow
                                    },
                                    '.MuiMarkElement-root': {
                                        stroke: '#fff',
                                        strokeWidth: 2,
                                        fill: CHART_COLORS.points,
                                        r: 6, // Biraz daha büyük noktalar
                                        ':hover': {
                                            r: 8, // Hover durumunda daha büyük noktalar
                                            strokeWidth: 3,
                                            filter: 'brightness(1.2)',
                                        },
                                    },
                                    '.MuiChartsTooltip-table': {
                                        backgroundColor: CHART_COLORS.dark,
                                        color: '#fff',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        fontSize: '12px',
                                        boxShadow: '0 4px 12px rgba(52, 71, 103, 0.3)',
                                    },
                                }}
                            />
                        ) : (
                            <MDTypography color="text" variant="button">
                                Gösterilecek proje verisi bulunamadı
                            </MDTypography>
                        )}
                    </MDBox>
                ),
                [labels, values, chartWidth, hasData]
            )}
        </Card>
    );
}

export default PassiveProjects;
